
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal, ArrowUpDown, MapIcon, Eye, EyeOff } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { 
  Sheet, 
  SheetContent, 
  SheetTitle,
  SheetClose
} from "@/components/ui/sheet";
import SortOptions, { SortOption } from "./SortOptions";
import FilterPanel, { FilterOptions } from "./FilterPanel";

interface ActionButtonsProps {
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  onToggleFilters: () => void;
  onToggleMapView: () => void;
  showMap: boolean;
  isFiltersOpen: boolean;
  filters: FilterOptions;
  setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
  applyFilters: () => void;
  resetFilters: () => void;
  activeFilterCount: number;
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  sortOption,
  onSortChange,
  onToggleFilters,
  onToggleMapView,
  showMap,
  isFiltersOpen,
  filters,
  setFilters,
  applyFilters,
  resetFilters,
  activeFilterCount
}) => {
  const { t } = useLanguage();
  const [openSortSheet, setOpenSortSheet] = useState(false);
  const [openFilterSheet, setOpenFilterSheet] = useState(false);
  
  return (
    <div className="w-full flex justify-between gap-2 mb-4 max-w-full overflow-hidden">
      {/* Sort Button */}
      <Sheet open={openSortSheet} onOpenChange={setOpenSortSheet}>
        <Button 
          onClick={() => setOpenSortSheet(true)}
          variant="outline" 
          className="w-1/3 shadow-sm rounded-lg justify-center h-12 px-2 py-1 text-center"
        >
          <div className="flex flex-col items-center">
            <ArrowUpDown className="h-5 w-5 mb-1" />
            <span className="text-xs break-words">{t('sort')}</span>
          </div>
        </Button>
        
        <SheetContent side="bottom" className="rounded-t-xl max-h-[80vh] overflow-y-auto z-50">
          <SheetTitle className="text-center mb-4">{t('sortBy')}</SheetTitle>
          <div className="px-2">
            <SortOptions 
              sortOption={sortOption}
              onSortChange={(option) => {
                onSortChange(option);
                setOpenSortSheet(false);
              }}
              isOpen={openSortSheet}
              onClose={() => setOpenSortSheet(false)}
              displayAsButtons={true}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Filter Button */}
      <Sheet open={openFilterSheet} onOpenChange={setOpenFilterSheet}>
        <Button 
          onClick={() => setOpenFilterSheet(true)}
          variant={isFiltersOpen || openFilterSheet ? "default" : "outline"}
          className={`w-1/3 shadow-sm rounded-lg justify-center h-12 px-2 py-1 text-center ${isFiltersOpen || openFilterSheet ? "bg-primary" : ""}`}
        >
          <div className="flex flex-col items-center">
            <SlidersHorizontal className="h-5 w-5 mb-1" />
            <span className="text-xs break-words">{t('filter')}</span>
          </div>
        </Button>
        
        <SheetContent side="bottom" className="rounded-t-xl max-h-[80vh] overflow-y-auto z-50">
          <SheetTitle className="text-center mb-4">{t('filter')}</SheetTitle>
          <div className="px-2 pb-16">
            <FilterPanel 
              filters={filters} 
              setFilters={setFilters}
              applyFilters={() => {
                applyFilters();
                setOpenFilterSheet(false);
              }}
              resetFilters={resetFilters}
              activeFilterCount={activeFilterCount}
              isOpen={openFilterSheet}
              onClose={() => setOpenFilterSheet(false)}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Map Button */}
      <Button 
        onClick={onToggleMapView}
        variant={showMap ? "default" : "outline"}
        className={`w-1/3 shadow-sm rounded-lg justify-center h-12 px-2 py-1 text-center ${showMap ? "bg-primary" : ""}`}
      >
        <div className="flex flex-col items-center">
          {showMap ? (
            <EyeOff className="h-5 w-5 mb-1" />
          ) : (
            <Eye className="h-5 w-5 mb-1" />
          )}
          <span className="text-xs break-words">
            {showMap ? t('hideMap') : t('showMap')}
          </span>
        </div>
      </Button>
    </div>
  );
};

export default ActionButtons;
